import React, { useEffect, useState } from 'react'
import { Card, List, Button, Typography, Space, Tag, Empty, Row, Col, Checkbox, App, Modal } from 'antd'
import { 
  VideoCameraOutlined, 
  PictureOutlined, 
  DownloadOutlined, 
  DeleteOutlined,
  EyeOutlined,
  SyncOutlined,
  HistoryOutlined
} from '@ant-design/icons'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import { on as onEvent } from '@/services/events'
import { apiClient } from '@/services/api'
import { getProviderName } from '@/utils/providerMapping'
import { COMMON_STYLES } from '@/styles/globalStyles'
import { useAuthStore } from '@/store/authStore'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography
  // Tabs.TabPane 已弃用，改用 items

const HistoryPage: React.FC = () => {
  const { message } = App.useApp()
  const [searchParams] = useSearchParams()
  
  // 从URL参数获取初始tab值
  const initialTab = searchParams.get('type') || 'all'
  const [activeTab, setActiveTab] = useState(initialTab)
  const [filterStatus, setFilterStatus] = useState('all')
  const [dateRange, setDateRange] = useState<any[]>([])

  const [data, setData] = useState<any[]>([])
  const [videoTotal, setVideoTotal] = useState<number>(0)
  const [imageTotal, setImageTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [batchDeleting, setBatchDeleting] = useState(false)
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set())
  const [refreshKey, setRefreshKey] = useState(0) // 用于强制刷新列表
  
  // 视频预览Modal状态
  const [videoPreviewModal, setVideoPreviewModal] = useState({
    visible: false,
    videoUrl: '',
    prompt: ''
  })
  
  // 图片预览Modal状态
  const [imagePreviewModal, setImagePreviewModal] = useState({
    visible: false,
    imageUrls: [] as string[],
    prompt: ''
  })

  // 加载真实数据（视频+图片）
  const [isLoading, setIsLoading] = useState(false)
  
  const loadData = async () => {
    // 避免重复加载
    if (isLoading) {
      console.log('⏸️  [历史列表] 已有加载任务进行中，跳过')
      return
    }
    
    try {
      setIsLoading(true)
      console.log('📥 [历史列表] 开始加载数据...')
      
      // 使用统一的 /history 接口，后端已实现联合分页
      const response = await apiClient.get(`/user/history?type=${activeTab}&page=${page}&limit=${limit}${filterStatus !== 'all' ? `&status=${filterStatus.toUpperCase()}` : ''}`)
      
      if (!response.success) {
        console.error('❌ [历史列表] 请求失败:', response)
        return
      }

      // 读取分页总数（用于统计）
      if (activeTab === 'all' && response.data?.summary) {
        // 全部类型：使用 summary 获取分别的总数
        const summary = response.data.summary
        setVideoTotal(summary.videoTotal ?? 0)
        setImageTotal(summary.imageTotal ?? 0)
      } else {
        // 视频或图片类型：使用 pagination.total
        const paginationTotal = response.data?.pagination?.total ?? 0
        if (activeTab === 'video') {
          setVideoTotal(paginationTotal)
          setImageTotal(0)
        } else {
          setVideoTotal(0)
          setImageTotal(paginationTotal)
        }
      }

      // 页码越界保护：若当前页超过总页数，自动回退到最后一页
      const paginationTotal = response.data?.pagination?.total ?? 0
      const totalPages = Math.max(1, Math.ceil(paginationTotal / limit))
      if (paginationTotal > 0 && page > totalPages) {
        console.warn(`⚠️  [历史列表] 当前页(${page})超过总页数(${totalPages})，回退到最后一页`)
        setPage(totalPages)
        return
      }

      const vlist = (response.data?.videoGenerations || []).map((g: any) => {
        if (g.status?.toLowerCase() === 'completed' && !g.outputVideo) {
          console.warn('⚠️  [历史列表] 已完成但无视频URL:', {
            id: g.id,
            status: g.status,
            outputVideo: g.outputVideo
          })
        }
        
        return {
          id: g.id,
          type: 'video',
          prompt: g.prompt,
          status: g.status?.toLowerCase(),
          thumbnail: null,
          result: g.outputVideo,
          createdAt: g.createdAt,
          model: g.model,
          duration: g.duration,
          cost: g.cost,
        }
      })
        
      const ilist = (response.data?.imageGenerations || []).map((g: any) => {
        let outputImages = []
        try {
          outputImages = typeof g.outputImages === 'string' ? JSON.parse(g.outputImages) : (Array.isArray(g.outputImages) ? g.outputImages : [])
        } catch {}
        const statusLower = g.status?.toLowerCase()
        const completedCount = Array.isArray(outputImages) ? outputImages.length : 0
        const displayCount = statusLower === 'completed' ? completedCount : (g.count || 0)
        return {
          id: g.id,
          type: 'image',
          prompt: g.prompt,
          status: statusLower,
          thumbnail: outputImages[0] || undefined,
          result: outputImages,
          createdAt: g.createdAt,
          model: g.model,
          count: displayCount,
          cost: g.cost,
        }
      })
      
      const allData = [...vlist, ...ilist].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setData(allData)
      console.log(`✅ [历史列表] 数据加载完成: ${vlist.length}个视频, ${ilist.length}个图片`)
    } catch (e: any) {
      console.error('❌ [历史列表] 数据加载失败:', e.message || e)
      if (e?.response?.status === 403) {
        message.warning('登录已过期，请重新登录')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    
    // 每30秒自动刷新一次数据（延长间隔，避免频繁超时）
    const timer = setInterval(() => {
      console.log('🔄 [历史列表] 定时刷新数据...')
      loadData()
    }, 30000)
    
    return () => clearInterval(timer)
  }, [page, limit, filterStatus, activeTab])

  // 切换类型或状态时重置到第1页
  useEffect(() => {
    setPage(1)
  }, [activeTab])

  // 实时合并：当有任务完成或进行中状态变化
  useEffect(() => {
    const merge = (p: any) => {
      console.log('🔔 [历史列表] 收到事件更新:', p)
      
      setData(prev => {
        const idx = prev.findIndex((i: any) => i.id === p.id)
        if (idx >= 0) {
          // 更新现有记录
          const next = prev.slice()
          const updated = {
            ...next[idx],
            status: p.status?.toLowerCase(),
            // 正确映射视频和图片的结果字段
            result: p.outputVideo || p.outputImages || next[idx].result,
          }
          
          console.log('✅ [历史列表] 更新记录:', {
            id: p.id,
            oldStatus: next[idx].status,
            newStatus: updated.status,
            oldResult: next[idx].result,
            newResult: updated.result
          })
          
          next[idx] = updated
          return next
        }
        
        // 新增记录
        const entry = { 
          id: p.id, 
          type: p.type || (p.outputImages ? 'image' : 'video'), 
          status: p.status?.toLowerCase(), 
          result: p.outputVideo || p.outputImages, 
          createdAt: new Date().toISOString(), 
          prompt: p.prompt || '' 
        }
        
        console.log('➕ [历史列表] 新增记录:', entry)
        return [entry, ...prev]
      })
    }
    onEvent('video_updated', merge)
    onEvent('image_updated', merge)
  }, [])

  // 根据activeTab和filterStatus过滤数据
  const filteredHistory = data.filter(item => {
    const typeMatch = activeTab === 'all' || 
      (activeTab === 'video' && item.type === 'video') ||
      (activeTab === 'image' && item.type === 'image')
    
    const statusMatch = filterStatus === 'all' || item.status === filterStatus
    
    return typeMatch && statusMatch
  })
  
  // 调试: 监控数据变化
  useEffect(() => {
    console.log(`📊 [前端] 数据状态更新:`, {
      总数: data.length,
      过滤后: filteredHistory.length,
      当前Tab: activeTab,
      当前筛选: filterStatus
    })
  }, [data, filteredHistory.length, activeTab, filterStatus])

  // 批量删除处理
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) {
      message.warning('请先选择要删除的记录')
      return
    }

    try {
      setBatchDeleting(true)
      
      // 分离视频和图片ID
      const videoIds = selectedIds.filter(id => {
        const item = data.find(d => d.id === id)
        return item?.type === 'video'
      })
      const imageIds = selectedIds.filter(id => {
        const item = data.find(d => d.id === id)
        return item?.type === 'image'
      })

      // 批量删除
      const deletePromises = []
      
      for (const id of videoIds) {
        deletePromises.push(apiClient.delete(`/video/generations/${id}`))
      }
      
      for (const id of imageIds) {
        deletePromises.push(apiClient.delete(`/image/generations/${id}`))
      }

      await Promise.all(deletePromises)

      message.success(`成功删除 ${selectedIds.length} 条记录`)
      
      // 更新本地数据
      setData(prev => prev.filter(item => !selectedIds.includes(item.id)))
      setSelectedIds([])

      // 重新加载数据以同步总量/分页（避免需手动刷新）
      await loadData()
    } catch (error: any) {
      message.error('删除失败: ' + (error?.message || '未知错误'))
    } finally {
      setBatchDeleting(false)
    }
  }

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredHistory.map(item => item.id))
    } else {
      setSelectedIds([])
    }
  }

  // 选择单个
  const handleSelectItem = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id])
    } else {
      setSelectedIds(prev => prev.filter(item => item !== id))
    }
  }

  // 预览视频（使用Modal居中展示）
  const handlePreviewVideo = (item: any) => {
    if (!item.result) {
      message.warning('视频文件不存在')
      return
    }
    
    setVideoPreviewModal({
      visible: true,
      videoUrl: item.result,
      prompt: item.prompt || '未命名作品'
    })
  }
  
  // 关闭视频预览Modal
  const handleCloseVideoPreview = () => {
    setVideoPreviewModal({
      visible: false,
      videoUrl: '',
      prompt: ''
    })
  }
  
  // 打开图片预览Modal
  const handlePreviewImage = (item: any) => {
    if (!item.result || item.result.length === 0) {
      message.warning('图片文件不存在')
      return
    }
    
    setImagePreviewModal({
      visible: true,
      imageUrls: item.result,
      prompt: item.prompt || '未命名作品'
    })
  }
  
  // 关闭图片预览Modal
  const handleCloseImagePreview = () => {
    setImagePreviewModal({
      visible: false,
      imageUrls: [],
      prompt: ''
    })
  }

  // 下载文件
  const handleDownload = async (url: string, filename: string) => {
    try {
      message.loading('正在准备下载...', 0)
      
      // 方案1: 尝试使用 fetch + blob 下载（推荐，不会刷新页面）
      try {
        const response = await fetch(url)
        const blob = await response.blob()
        
        // 创建本地 Blob URL
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 清理资源
        window.URL.revokeObjectURL(blobUrl)
        
        message.destroy()
        message.success('下载成功')
      } catch (fetchError) {
        // 方案2: 如果 fetch 失败（可能是 CORS 问题），使用新窗口打开
        console.warn('Fetch下载失败，切换到新窗口下载:', fetchError)
        window.open(url, '_blank')
        message.destroy()
        message.success('已在新窗口打开下载')
      }
    } catch (error) {
      console.error('下载失败:', error)
      message.destroy()
      message.error('下载失败，请重试')
    }
  }

  // 下载视频
  const handleDownloadVideo = (item: any) => {
    if (!item.result) {
      message.warning('视频文件不存在')
      return
    }
    
    const filename = `${item.prompt || '未命名作品'}_${item.id}.mp4`.replace(/[^\w\s-]/g, '')
    handleDownload(item.result, filename)
  }

  // 下载图片
  const handleDownloadImage = (item: any) => {
    if (!item.result || item.result.length === 0) {
      message.warning('图片文件不存在')
      return
    }
    
    if (item.result.length === 1) {
      // 单张图片
      const filename = `${item.prompt || '未命名作品'}_${item.id}.png`.replace(/[^\w\s-]/g, '')
      handleDownload(item.result[0], filename)
    } else {
      // 多张图片，逐个下载
      item.result.forEach((url: string, index: number) => {
        const filename = `${item.prompt || '未命名作品'}_${item.id}_${index + 1}.png`.replace(/[^\w\s-]/g, '')
        setTimeout(() => {
          handleDownload(url, filename)
        }, index * 500) // 延迟500ms避免浏览器阻止
      })
      message.success(`开始下载 ${item.result.length} 张图片...`)
    }
  }

  // 收藏/取消收藏
  const handleFavorite = async (item: any) => {
    try {
      // 这里可以调用后端API来实现收藏功能
      // 暂时用本地存储模拟
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      const existingIndex = favorites.findIndex((fav: any) => fav.id === item.id)
      
      if (existingIndex >= 0) {
        // 取消收藏
        favorites.splice(existingIndex, 1)
        message.success('已取消收藏')
      } else {
        // 添加收藏
        favorites.push({
          id: item.id,
          type: item.type,
          prompt: item.prompt,
          result: item.result,
          createdAt: item.createdAt,
          favoritedAt: new Date().toISOString()
        })
        message.success('已添加到收藏')
      }
      
      localStorage.setItem('favorites', JSON.stringify(favorites))
    } catch (error) {
      console.error('收藏操作失败:', error)
      message.error('操作失败，请重试')
    }
  }

  // 单个删除
  const handleDeleteItem = async (item: any) => {
    try {
      const endpoint = item.type === 'video' ? `/video/generations/${item.id}` : `/image/generations/${item.id}`
      await apiClient.delete(endpoint)
      
      message.success('删除成功')
      
      // 更新本地数据
      setData(prev => prev.filter(d => d.id !== item.id))
      
      // 如果该item被选中，从选中列表中移除
      setSelectedIds(prev => prev.filter(id => id !== item.id))

      // 重新加载数据以同步总量/分页
      await loadData()
    } catch (error: any) {
      console.error('删除失败:', error)
      message.error('删除失败: ' + (error?.message || '未知错误'))
    }
  }

  // 检查是否已收藏
  const isFavorited = (item: any) => {
    try {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      return favorites.some((fav: any) => fav.id === item.id)
    } catch {
      return false
    }
  }

  // 同步单个任务状态
  const handleSyncStatus = async (id: string, type: string) => {
    setSyncingIds(prev => new Set(prev).add(id))
    
    try {
      const endpoint = type === 'video' ? `/video/generations/${id}/sync` : `/image/generations/${id}/sync`
      console.log(`🔄 [前端] 开始同步任务:`, { id, type, endpoint })
      
      const response = await apiClient.post(endpoint)
      
      console.log(`📥 [前端] 同步响应 (完整):`, response)
      console.log(`📥 [前端] response.data:`, response.data)
      
      // 正确解析响应格式
      // 后端返回: { success: true, message: "...", data: { status: "...", ... } }
      const apiResponse = response.data
      const isSuccess = apiResponse.success !== false
      const msg = apiResponse.message || '同步成功'
      const responseData = apiResponse.data || {}
      
      console.log(`📊 [前端] 解析结果:`, { 
        isSuccess, 
        msg, 
        responseData 
      })
      
      if (isSuccess) {
        // 显示提示消息
        if (responseData.status === 'FAILED') {
          // 如果是无法恢复的失败任务，显示更明确的提示
          if (responseData.cannotRecover) {
            message.error({
              content: msg,
              duration: 6
            })
          } else {
            message.warning(msg)
          }
        } else if (responseData.status === 'COMPLETED') {
          message.success(msg)
        } else {
          message.info(msg)
        }
        
        // 更新本地数据 - 使用函数式更新确保获取最新状态
        console.log(`📋 [前端] 开始更新数据，查找 ID: ${id}`)
        
        setData(prevData => {
          console.log(`📋 [前端] setData 回调执行，当前 data 总数: ${prevData.length}`)
          
          const updatedData = prevData.map((item, index) => {
            if (item.id === id) {
              const newStatus = (responseData.status || 'failed').toLowerCase()
              const newResult = responseData.outputVideo || responseData.outputImages || item.result
              
              console.log(`✅ [前端] 找到匹配项 (索引 ${index}):`, { 
                itemId: item.id,
                oldStatus: item.status, 
                newStatus,
                oldResult: item.result,
                newResult,
                hasResult: !!newResult,
                creditsRefunded: responseData.creditsRefunded
              })
              
              const updated = {
                ...item,
                status: newStatus,
                result: newResult
              }
              
              console.log(`🔄 [前端] 更新后的数据:`, updated)
              return updated
            }
            return item
          })
          
          console.log(`🔄 [前端] 返回新数据数组，总数: ${updatedData.length}`)
          return updatedData
        })
        
        console.log(`✅ [前端] setData 已调用`)
        
        // 强制刷新列表渲染
        const newRefreshKey = Date.now()
        setRefreshKey(newRefreshKey)
        console.log(`🔄 [前端] 强制刷新列表渲染, refreshKey: ${newRefreshKey}`)
        
        // 根据不同情况决定是否刷新页面
        if (responseData.creditsRefunded && responseData.creditsRefunded > 0) {
          // 🔥 修复：退还了积分，刷新用户信息而不是整页刷新
          console.log(`💰 [前端] 积分已退还: ${responseData.creditsRefunded}，刷新用户积分`)
          try {
            await useAuthStore.getState().refreshUser();
            console.log(`✅ [前端] 用户积分已刷新`)
          } catch (refreshError) {
            console.warn('⚠️ [前端] 刷新用户积分失败，使用整页刷新:', refreshError);
            setTimeout(() => {
              window.location.reload()
            }, 1500)
          }
        } else if (responseData.status === 'COMPLETED') {
          // 任务完成，刷新页面显示新按钮
          console.log(`🎬 [前端] 任务完成，2秒后刷新页面以显示预览按钮`)
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        } else if (responseData.alreadyRefunded || responseData.status === 'FAILED') {
          // 已失败且积分之前已退还，刷新页面确保UI同步
          console.log(`ℹ️  [前端] 任务已失败，2秒后刷新页面确保UI更新`)
          setTimeout(() => {
            console.log(`🔄 [前端] 强制刷新页面`)
            window.location.reload()
          }, 2000)
        } else {
          // 仍在处理中，不刷新页面
          console.log(`✅ [前端] 状态已更新为: ${responseData.status}，无需刷新页面`)
        }
      }
    } catch (error: any) {
      console.error(`❌ [前端] 同步失败:`, error)
      console.error(`❌ [前端] 错误响应:`, error?.response?.data)
      
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误'
      message.error({
        content: '同步失败: ' + errorMsg,
        duration: 5
      })
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const renderVideoItem = (item: any) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -4, scale: 1.005 }}
    >
      <Card 
        className="mb-4 border border-gray-100 hover:border-blue-200 cursor-pointer"
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease'
        }}
        styles={{ body: { padding: '20px' } }}
        hoverable
      >
        <Row gutter={20}>
          <Col span={6}>
            <div 
              className="relative group"
              onClick={() => {
                // 如果视频已完成，点击缩略图即可预览
                if (item.status === 'completed' && item.result) {
                  handlePreviewVideo(item)
                }
              }}
              style={{ 
                cursor: item.status === 'completed' && item.result ? 'pointer' : 'default' 
              }}
            >
              {item.result ? (
                <video
                  className="w-full h-36 rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300"
                  style={{ 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                  src={item.result}
                  preload="metadata"
                  muted
                  playsInline
                  onMouseEnter={(e) => {
                    const video = e.currentTarget
                    video.play().catch(() => {})
                  }}
                  onMouseLeave={(e) => {
                    const video = e.currentTarget
                    video.pause()
                    video.currentTime = 0
                  }}
                  onError={(e) => {
                    const el = e.currentTarget as HTMLVideoElement
                    const parent = el.parentElement
                    if (parent) {
                      el.style.display = 'none'
                      parent.innerHTML = `
                        <div class="w-full h-36 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center shadow-sm" style="border-radius: 12px">
                          <svg class="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 002 0V7zm-1 8a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" clip-rule="evenodd" />
                          </svg>
                        </div>
                      `
                    }
                  }}
                />
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300" style={{ borderRadius: '12px' }}>
                  <VideoCameraOutlined className="text-4xl text-blue-400" />
                </div>
              )}
              {item.status === 'processing' && (
                <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <div className="text-white text-center">
                    <div className="animate-spin w-8 h-8 border-3 border-white border-t-transparent rounded-full mx-auto mb-3"></div>
                    <Text className="text-white text-sm font-medium">生成中...</Text>
                  </div>
                </div>
              )}
              {item.status === 'completed' && item.result && (
                <>
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
                  {/* 悬停时显示播放图标 */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-xl flex items-center justify-center transition-all duration-300">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                        <div className="w-0 h-0 border-l-[20px] border-l-blue-500 border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent ml-1"></div>
                      </div>
                      <Text className="text-white text-sm font-medium mt-2 block text-center">点击预览</Text>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Col>
          <Col span={18}>
            <div className="h-full flex flex-col justify-between">
              <div>
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  className="mb-3 text-gray-800 font-semibold text-responsive-base"
                  style={{ lineHeight: 1.5 }}
                >
                  {item.prompt || '未命名作品'}
                </Paragraph>
                <Space wrap className="mb-3" size="small">
                  <Tag color="blue" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <VideoCameraOutlined /> {getProviderName(item.model, 'video')}
                  </Tag>
                  <Tag color="purple" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    ⏱️ {item.duration}秒
                  </Tag>
                  <Tag color="orange" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    💎 {item.cost}积分
                  </Tag>
                </Space>
              </div>
              <div className="flex items-center justify-between mt-3">
                <Space size="small">
                  {/* 只在非完成状态时显示状态标签 */}
                  {item.status !== 'completed' && (
                    <Tag 
                      color={item.status === 'processing' ? 'processing' : item.status === 'failed' ? 'error' : 'default'}
                      style={{ borderRadius: '12px' }}
                    >
                      {item.status === 'processing' ? '⏳ 处理中' : item.status === 'failed' ? '✗ 失败' : '⏸ 待处理'}
                    </Tag>
                  )}
                  <Text type="secondary" className="text-xs">
                    {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </Space>
                <div className="flex items-center gap-1 flex-nowrap">
                  {item.status === 'completed' && (
                    <>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewVideo(item)}
                        className="border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 flex-shrink-0"
                        style={{ 
                          borderRadius: '20px', 
                          fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                          padding: '0 clamp(8px, 1.5vw, 12px)'
                        }}
                      >
                        <span className="hidden sm:inline">预览</span>
                      </Button>
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />} 
                        type="primary"
                        onClick={() => handleDownloadVideo(item)}
                        className="hover:shadow-lg transition-all duration-200 flex-shrink-0"
                        style={{ 
                          borderRadius: '20px', 
                          fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                          padding: '0 clamp(8px, 1.5vw, 12px)'
                        }}
                      >
                        <span className="hidden sm:inline">下载</span>
                      </Button>
                    </>
                  )}
                  {(item.status === 'failed' || item.status === 'pending' || item.status === 'processing') && (
                    <Button 
                      size="small" 
                      icon={<SyncOutlined spin={syncingIds.has(item.id)} />}
                      onClick={() => handleSyncStatus(item.id, 'video')}
                      loading={syncingIds.has(item.id)}
                      className="border-orange-300 hover:border-orange-400 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200 flex-shrink-0"
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                        padding: '0 clamp(8px, 1.5vw, 12px)'
                      }}
                    >
                      <span className="hidden sm:inline">同步状态</span>
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    danger
                    onClick={() => handleDeleteItem(item)}
                    className="hover:shadow-md transition-all duration-200 flex-shrink-0"
                    style={{ 
                      borderRadius: '20px', 
                      fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                      padding: '0 clamp(8px, 1.5vw, 12px)'
                    }}
                  >
                    <span className="hidden sm:inline">删除</span>
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </motion.div>
  )

  const renderImageItem = (item: any) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -4, scale: 1.005 }}
    >
      <Card 
        className="mb-4 border border-gray-100 hover:border-purple-200 cursor-pointer"
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease'
        }}
        styles={{ body: { padding: '20px' } }}
        hoverable
      >
        <Row gutter={20}>
          <Col span={6}>
            <div 
              className="relative group"
              onClick={() => {
                // 点击缩略图预览
                if (item.status === 'completed' && item.result && item.result.length > 0) {
                  handlePreviewImage(item)
                }
              }}
              style={{ 
                cursor: item.status === 'completed' && item.result ? 'pointer' : 'default' 
              }}
            >
              {item.thumbnail || (item.result && item.result.length > 0) ? (
                <div className="w-full h-36 overflow-hidden" style={{ borderRadius: '12px' }}>
                  <img
                    src={item.thumbnail || item.result[0]}
                    alt="Generated image"
                    loading="lazy"
                    className="w-full h-full shadow-sm group-hover:shadow-md transition-all duration-300"
                    style={{ 
                      borderRadius: '12px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      width: '100%',
                      height: '100%'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="w-full h-36 bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-lg flex items-center justify-center shadow-sm" style="border-radius: 12px">
                            <svg class="text-4xl text-purple-400" fill="currentColor" viewBox="0 0 20 20" width="40" height="40">
                              <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                            </svg>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              ) : (
                <div 
                  className="w-full h-36 bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300" 
                  style={{ borderRadius: '12px' }}
                >
                  <PictureOutlined className="text-4xl text-purple-400" />
                </div>
              )}
              
              {/* 状态遮罩层 */}
              {item.status === 'processing' && (
                <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl flex items-center justify-center backdrop-blur-sm pointer-events-none">
                  <div className="text-white text-center">
                    <div className="animate-spin w-8 h-8 border-3 border-white border-t-transparent rounded-full mx-auto mb-3"></div>
                    <Text className="text-white text-sm font-medium">生成中...</Text>
                  </div>
                </div>
              )}
              
              {/* 完成状态标记 */}
              {item.status === 'completed' && item.thumbnail && (
                <>
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg pointer-events-none"></div>
                  {/* 悬停时显示预览提示 - 不阻止点击 */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-xl flex items-center justify-center transition-all duration-300 pointer-events-none">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                        <EyeOutlined className="text-lg text-purple-500" />
                      </div>
                      <Text className="text-white text-sm font-medium mt-2 block text-center drop-shadow-lg">点击预览</Text>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Col>
          <Col span={18}>
            <div className="h-full flex flex-col justify-between">
              <div>
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  className="mb-3 text-gray-800 font-semibold text-responsive-base"
                  style={{ lineHeight: 1.5 }}
                >
                  {item.prompt || '未命名作品'}
                </Paragraph>
                <Space wrap className="mb-3" size="small">
                  <Tag color="purple" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <PictureOutlined /> {getProviderName(item.model, 'image')}
                  </Tag>
                  <Tag color="cyan" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    🖼️ {(item.status === 'completed' && Array.isArray(item.result)) ? item.result.length : (item.count || 0)}张
                  </Tag>
                  <Tag color="orange" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    💎 {item.cost}积分
                  </Tag>
                </Space>
              </div>
              <div className="flex items-center justify-between mt-3">
                <Space size="small">
                  {/* 只在非完成状态时显示状态标签 */}
                  {item.status !== 'completed' && (
                    <Tag 
                      color={item.status === 'processing' ? 'processing' : item.status === 'failed' ? 'error' : 'default'}
                      style={{ borderRadius: '12px' }}
                    >
                      {item.status === 'processing' ? '⏳ 处理中' : item.status === 'failed' ? '✗ 失败' : '⏸ 待处理'}
                    </Tag>
                  )}
                  <Text type="secondary" className="text-xs">
                    {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </Space>
                <Space wrap size="small">
                  {item.status === 'completed' && (
                    <>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewImage(item)}
                        className="border-gray-300 hover:border-purple-400 hover:bg-purple-50 transition-all duration-200"
                        style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                      >
                        预览 {item.result && item.result.length > 1 ? `(${item.result.length}张)` : ''}
                      </Button>
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />} 
                        type="primary"
                        onClick={() => handleDownloadImage(item)}
                        className="hover:shadow-lg transition-all duration-200"
                        style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                      >
                        下载
                      </Button>
                    </>
                  )}
                  {(item.status === 'failed' || item.status === 'pending' || item.status === 'processing') && (
                    <Button 
                      size="small" 
                      icon={<SyncOutlined spin={syncingIds.has(item.id)} />}
                      onClick={() => handleSyncStatus(item.id, 'image')}
                      loading={syncingIds.has(item.id)}
                      className="border-orange-300 hover:border-orange-400 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200"
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      同步状态
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    danger
                    onClick={() => handleDeleteItem(item)}
                    className="hover:shadow-md transition-all duration-200"
                    style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                  >
                    删除
                  </Button>
                </Space>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </motion.div>
  )

  return (
    <div className="min-h-screen w-full relative">
      {/* 移动端背景装饰 */}
      <div className="md:hidden absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-100/50 to-transparent pointer-events-none"></div>
      <div className="unified-container relative z-10 py-2 md:py-6">
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col"
        >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full">
            <motion.div
              {...COMMON_STYLES.pageEnter}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            >
            {/* 左侧：标题和副标题 */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">📚</span>
                <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  我的作品库
                </h1>
              </div>
              <Paragraph className="unified-text-secondary text-base md:ml-12 !mb-0">
                查看、管理和下载您的所有AI创作
              </Paragraph>
            </div>
            
            {/* 右侧：特色标签 */}
            <div className="flex flex-wrap gap-2 md:justify-end">
              <Tag icon={<HistoryOutlined />} className="unified-tag bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700">
                {videoTotal + imageTotal} 作品
              </Tag>
              <Tag icon={<VideoCameraOutlined />} className="unified-tag bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700">
                {videoTotal} 视频
              </Tag>
              <Tag icon={<PictureOutlined />} className="unified-tag bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700">
                {imageTotal} 图片
              </Tag>
            </div>
          </motion.div>
          </div>


          {/* 筛选器 */}
          <Card className="mb-6 border border-gray-100" style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }} styles={{ body: { padding: '20px 24px' } }}>
            <div className="flex flex-wrap gap-4 items-center justify-between">
              {/* 左侧：筛选器 */}
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-3">
                  <Text strong className="text-gray-700 text-responsive-sm">类型</Text>
                  <Space size="small">
                    <Button
                      type={activeTab === 'all' ? 'primary' : 'default'}
                      onClick={() => setActiveTab('all')}
                      className={activeTab === 'all' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      全部
                    </Button>
                    <Button
                      type={activeTab === 'video' ? 'primary' : 'default'}
                      icon={<VideoCameraOutlined />}
                      onClick={() => setActiveTab('video')}
                      className={activeTab === 'video' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      视频
                    </Button>
                    <Button
                      type={activeTab === 'image' ? 'primary' : 'default'}
                      icon={<PictureOutlined />}
                      onClick={() => setActiveTab('image')}
                      className={activeTab === 'image' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      图像
                    </Button>
                  </Space>
                </div>
                
                <div className="flex items-center gap-3">
                  <Text strong className="text-gray-700 text-responsive-sm">状态</Text>
                  <Space size="small">
                    <Button
                      type={filterStatus === 'all' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('all')}
                      size="small"
                      className={filterStatus === 'all' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      全部
                    </Button>
                    <Button
                      type={filterStatus === 'pending' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('pending')}
                      size="small"
                      className={filterStatus === 'pending' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      待处理
                    </Button>
                    <Button
                      type={filterStatus === 'processing' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('processing')}
                      size="small"
                      className={filterStatus === 'processing' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      处理中
                    </Button>
                    <Button
                      type={filterStatus === 'completed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('completed')}
                      size="small"
                      className={filterStatus === 'completed' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      已完成
                    </Button>
                    <Button
                      type={filterStatus === 'failed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('failed')}
                      size="small"
                      className={filterStatus === 'failed' ? '' : 'border-gray-300'}
                      style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}
                    >
                      失败
                    </Button>
                  </Space>
                </div>
              </div>
              
              {/* 右侧：批量操作和统计 */}
              <div className="flex items-center gap-4">
                {selectedIds.length > 0 ? (
                  <Space>
                    <Text strong className="text-orange-600">
                      已选 <span className="font-bold">{selectedIds.length}</span> 条
                    </Text>
                    <Button
                      danger
                      type="primary"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={handleBatchDelete}
                      loading={batchDeleting}
                      style={{ borderRadius: '20px' }}
                    >
                      批量删除
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedIds([])}
                      style={{ borderRadius: '20px' }}
                    >
                      取消选择
                    </Button>
                  </Space>
                ) : (
                  <Text strong className="text-gray-700 text-responsive-sm">
                    共 <span className="text-blue-600 font-bold">{
                      activeTab === 'video' ? videoTotal : activeTab === 'image' ? imageTotal : (videoTotal + imageTotal)
                    }</span> 条记录
                  </Text>
                )}
              </div>
            </div>
          </Card>

          {/* 批量选择提示 */}
          {filteredHistory.length > 0 && (
            <div className="mb-4 flex items-center gap-3">
              <Checkbox
                checked={selectedIds.length === filteredHistory.length && filteredHistory.length > 0}
                indeterminate={selectedIds.length > 0 && selectedIds.length < filteredHistory.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                <Text className="text-sm">全选当前页</Text>
              </Checkbox>
              {selectedIds.length > 0 && (
                <Text type="secondary" className="text-xs">
                  已选择 {selectedIds.length} / {filteredHistory.length} 条
                </Text>
              )}
            </div>
          )}

          {/* 内容列表 */}
          <Card 
            className="border border-gray-100 mx-0"
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
            styles={{ body: { padding: '0' } }}
          >
            {/* 顶部装饰渐变线 */}
            <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1 w-full"></div>

            {filteredHistory.length === 0 ? (
              <div style={{ padding: '60px 20px' }}>
              <Empty
                  description={
                    <div>
                      <Text className="text-gray-500 text-responsive-sm">暂无生成记录</Text>
                      <br />
                      <Text className="text-gray-400 text-xs mt-2">
                        开始创作您的第一个AI作品吧
                      </Text>
                    </div>
                  }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
              </div>
            ) : (
              <List
                key={refreshKey}
                dataSource={filteredHistory}
                pagination={{ 
                  current: page,
                  pageSize: limit,
                  total: (() => {
                    if (activeTab === 'video') {
                      return Math.max(videoTotal, filteredHistory.length)
                    }
                    if (activeTab === 'image') {
                      return Math.max(imageTotal, filteredHistory.length)
                    }
                    // 全部类型：使用合并总数
                    return Math.max((videoTotal + imageTotal), filteredHistory.length)
                  })(),
                  showSizeChanger: true,
                  pageSizeOptions: ['10','20'],
                  showQuickJumper: true,
                  onChange: (p) => {
                    // 根据当前 tab 计算总页数
                    let total: number
                    if (activeTab === 'video') {
                      total = Math.max(videoTotal, filteredHistory.length)
                    } else if (activeTab === 'image') {
                      total = Math.max(imageTotal, filteredHistory.length)
                    } else {
                      // 全部类型：使用合并总数
                      total = Math.max((videoTotal + imageTotal), filteredHistory.length)
                    }
                    const totalPages = Math.max(1, Math.ceil(total / limit))
                    const next = Math.min(Math.max(1, p), totalPages)
                    if (next !== page) setPage(next)
                    // 平滑滚动到列表顶部，避免“页面跳出”的突兀感
                    try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch {}
                  },
                  onShowSizeChange: (p, size) => { setPage(1); setLimit(size); try { window.scrollTo({ top: 0, behavior: 'smooth' }) } catch {} },
                  showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条（每页 ${limit} 条）`,
                  style: { textAlign: 'center', padding: '20px 0' }
                }}
                renderItem={(item) => (
                  <List.Item className="!px-0 !border-0" style={{ padding: '12px 20px' }}>
                    <div className="w-full flex items-start gap-3">
                      {/* 复选框 */}
                      <div className="pt-6">
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                        />
                      </div>
                      
                      {/* 内容卡片 */}
                      <div className="flex-1">
                        {item.type === 'video' ? renderVideoItem(item) : renderImageItem(item)}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </motion.div>
      </div>
      
      {/* 视频预览Modal */}
      <Modal
        open={videoPreviewModal.visible}
        onCancel={handleCloseVideoPreview}
        footer={null}
        width="80vw"
        centered
        destroyOnHidden
        styles={{ body: { padding: '24px' } }}
      >
        <div className="text-center">
          <Title level={4} className="mb-4">
            {videoPreviewModal.prompt}
          </Title>
          <video
            controls
            autoPlay
            style={{
              width: '100%',
              maxHeight: '70vh',
              borderRadius: '8px',
              backgroundColor: '#000'
            }}
          >
            <source src={videoPreviewModal.videoUrl} type="video/mp4" />
            您的浏览器不支持视频播放。
          </video>
          
          {/* 底部操作按钮 */}
          <div className="mt-6 pt-4 border-t-2 border-gray-200">
            <Space size="large">
              <Button 
                type="primary" 
                size="large"
                icon={<DownloadOutlined />}
                onClick={() => {
                  handleDownload(videoPreviewModal.videoUrl, `${videoPreviewModal.prompt}.mp4`)
                }}
                style={{
                  height: '44px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  minWidth: '120px'
                }}
              >
                下载视频
              </Button>
              <Button 
                size="large"
                onClick={handleCloseVideoPreview}
                style={{
                  height: '44px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  minWidth: '120px'
                }}
              >
                关闭
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
      
      {/* 图片预览Modal */}
      <Modal
        open={imagePreviewModal.visible}
        onCancel={handleCloseImagePreview}
        footer={null}
        width="80vw"
        centered
        destroyOnHidden
        styles={{ body: { padding: '24px' } }}
      >
        <div className="text-center">
          <Title level={4} className="mb-4">
            {imagePreviewModal.prompt}
          </Title>
          
          {/* 图片显示 */}
          <div>
            {imagePreviewModal.imageUrls.length === 1 ? (
              // 单张图片
              <img
                src={imagePreviewModal.imageUrls[0]}
                alt="预览图片"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  borderRadius: '8px',
                  display: 'block',
                  margin: '0 auto'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="16"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                }}
              />
            ) : (
              // 多张图片 - 横向滚动
              <div>
                <div className="flex gap-3 justify-center overflow-x-auto pb-4" style={{ maxWidth: '100%' }}>
                  {imagePreviewModal.imageUrls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`图片 ${index + 1}`}
                      style={{
                        maxHeight: '60vh',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        // 点击切换为主图
                        const newUrls = [...imagePreviewModal.imageUrls];
                        [newUrls[0], newUrls[index]] = [newUrls[index], newUrls[0]];
                        setImagePreviewModal({ ...imagePreviewModal, imageUrls: newUrls });
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ))}
                </div>
                <div className="text-gray-500 text-sm mb-2">
                  共 {imagePreviewModal.imageUrls.length} 张图片，点击切换查看
                </div>
              </div>
            )}
          </div>
          
          {/* 底部操作按钮 - 与视频预览保持一致 */}
          <div className="mt-6 pt-4 border-t-2 border-gray-200">
            <Space size="large">
              <Button 
                type="primary" 
                size="large"
                icon={<DownloadOutlined />}
                onClick={() => {
                  // 下载所有图片
                  imagePreviewModal.imageUrls.forEach((url, index) => {
                    setTimeout(() => {
                      handleDownload(url, `${imagePreviewModal.prompt}_${index + 1}.jpg`)
                    }, index * 500);
                  });
                }}
                style={{
                  height: '44px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  minWidth: '120px'
                }}
              >
                下载图片
              </Button>
              <Button 
                size="large"
                onClick={handleCloseImagePreview}
                style={{
                  height: '44px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  minWidth: '120px'
                }}
              >
                关闭
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default HistoryPage
